import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Users, ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AttendanceReport = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [attendanceSheet, setAttendanceSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAttendance, setDateAttendance] = useState([]);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const STATUS_COLORS = {
    "Present": "bg-success/20 text-success",
    "P": "bg-success text-white",
    "Absent": "bg-error/20 text-error",
    "A": "bg-error text-white",
    "Half Day": "bg-warning/20 text-warning",
    "H": "bg-warning text-white",
    "On Leave": "bg-primary/20 text-primary",
    "L": "bg-primary text-white",
    "Not Marked": "bg-slate-100 text-slate-400",
    "-": "bg-slate-100 text-slate-400"
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchAttendanceSheet();
    }
  }, [selectedProject, currentMonth, currentYear]);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch projects");
    }
  };

  const fetchAttendanceSheet = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/attendance/monthly-sheet/${selectedProject}?month=${currentMonth}&year=${currentYear}`);
      // Transform the data to match our expected format
      const data = response.data;
      const transformedData = {
        ...data,
        attendance_sheet: (data.employees || []).map(emp => ({
          ...emp,
          // Transform days from {status, ot} to just status code for display
          days: Object.fromEntries(
            Object.entries(emp.days || {}).map(([day, val]) => [day, typeof val === 'object' ? val.status : val])
          ),
          overtime: Object.fromEntries(
            Object.entries(emp.days || {}).map(([day, val]) => [day, typeof val === 'object' ? val.ot : 0])
          )
        })),
        overall_summary: calculateOverallSummary(data.employees || [])
      };
      setAttendanceSheet(transformedData);
      generateCalendarData(transformedData);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallSummary = (employees) => {
    let total_present = 0, total_absent = 0, total_half_day = 0, total_leave = 0, total_overtime = 0;
    employees.forEach(emp => {
      total_present += emp.summary?.P || 0;
      total_absent += emp.summary?.A || 0;
      total_half_day += emp.summary?.H || 0;
      total_leave += emp.summary?.L || 0;
      total_overtime += emp.summary?.OT || 0;
    });
    return { total_present, total_absent, total_half_day, total_leave, total_overtime };
  };

  const generateCalendarData = (data) => {
    if (!data) return;
    
    const totalDays = data.total_days;
    const daysData = [];
    
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = new Date(dateStr).getDay();
      
      let present = 0, absent = 0, halfDay = 0, onLeave = 0, notMarked = 0;
      
      data.attendance_sheet.forEach(emp => {
        const status = emp.days[day];
        if (status === "P") present++;
        else if (status === "A") absent++;
        else if (status === "H") halfDay++;
        else if (status === "L") onLeave++;
        else notMarked++;
      });
      
      daysData.push({
        day,
        dateStr,
        dayOfWeek,
        present,
        absent,
        halfDay,
        onLeave,
        notMarked,
        total: data.attendance_sheet.length
      });
    }
    
    setCalendarData(daysData);
  };

  const handleDateClick = async (dateStr, day) => {
    setSelectedDate({ dateStr, day });
    
    // Filter attendance for that date
    if (attendanceSheet) {
      const dayAttendance = attendanceSheet.attendance_sheet.map(emp => ({
        ...emp,
        status: emp.days[day] || "-",
        overtime: emp.overtime?.[day] || 0
      }));
      setDateAttendance(dayAttendance);
      setDrilldownOpen(true);
    }
  };

  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getStatusBadge = (status) => {
    const colorClass = STATUS_COLORS[status] || STATUS_COLORS["-"];
    const statusText = status === "P" ? "Present" : 
                       status === "A" ? "Absent" : 
                       status === "H" ? "Half Day" : 
                       status === "L" ? "On Leave" : "Not Marked";
    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${colorClass}`}>
        {statusText}
      </span>
    );
  };

  const getDepartments = () => {
    if (!attendanceSheet) return [];
    const depts = new Set(attendanceSheet.attendance_sheet.map(e => e.department));
    return Array.from(depts).filter(d => d);
  };

  const filteredDateAttendance = dateAttendance.filter(emp => 
    !departmentFilter || emp.department === departmentFilter
  );

  const exportToExcel = () => {
    if (!attendanceSheet) return;
    
    // Create CSV content
    let csv = "Employee Code,Name,Department";
    for (let i = 1; i <= attendanceSheet.total_days; i++) {
      csv += `,${i}`;
    }
    csv += ",Present,Absent,Half Day,Leave,OT Hours\n";
    
    attendanceSheet.attendance_sheet.forEach(emp => {
      csv += `${emp.employee_code},${emp.name},${emp.department}`;
      for (let i = 1; i <= attendanceSheet.total_days; i++) {
        csv += `,${emp.days[i] || "-"}`;
      }
      csv += `,${emp.summary.P},${emp.summary.A},${emp.summary.H},${emp.summary.L},${emp.summary.OT}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance_${MONTHS[currentMonth-1]}_${currentYear}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Attendance sheet exported");
  };

  return (
    <div data-testid="attendance-report-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">ATTENDANCE REPORT</h1>
          <p className="text-slate-600 mt-2 font-body">Calendar view of workforce attendance</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            data-testid="project-select"
            className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select Project</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button onClick={exportToExcel} className="btn-outline flex items-center space-x-2" data-testid="export-btn">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center space-x-6 mb-8">
        <button onClick={goToPreviousMonth} className="p-2 hover:bg-slate-100 rounded-full" data-testid="prev-month-btn">
          <ChevronLeft className="w-6 h-6 text-slate-600" />
        </button>
        <h2 className="font-heading font-bold text-2xl text-primary min-w-[200px] text-center" data-testid="current-month">
          {MONTHS[currentMonth - 1]} {currentYear}
        </h2>
        <button onClick={goToNextMonth} className="p-2 hover:bg-slate-100 rounded-full" data-testid="next-month-btn">
          <ChevronRight className="w-6 h-6 text-slate-600" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading attendance data...</div>
      ) : attendanceSheet ? (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <div className="stat-card border-l-4 border-l-success">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Present</p>
              <p className="font-heading font-black text-3xl text-success font-mono" data-testid="total-present">
                {attendanceSheet.overall_summary?.total_present || 0}
              </p>
            </div>
            <div className="stat-card border-l-4 border-l-error">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Absent</p>
              <p className="font-heading font-black text-3xl text-error font-mono" data-testid="total-absent">
                {attendanceSheet.overall_summary?.total_absent || 0}
              </p>
            </div>
            <div className="stat-card border-l-4 border-l-warning">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Half Days</p>
              <p className="font-heading font-black text-3xl text-warning font-mono" data-testid="total-halfday">
                {attendanceSheet.overall_summary?.total_half_day || 0}
              </p>
            </div>
            <div className="stat-card border-l-4 border-l-primary">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">On Leave</p>
              <p className="font-heading font-black text-3xl text-primary font-mono" data-testid="total-leave">
                {attendanceSheet.overall_summary?.total_leave || 0}
              </p>
            </div>
            <div className="stat-card border-l-4 border-l-accent">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total OT Hours</p>
              <p className="font-heading font-black text-3xl text-accent font-mono" data-testid="total-overtime">
                {attendanceSheet.overall_summary?.total_overtime?.toFixed(1) || 0}
              </p>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="stat-card mb-8">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-primary" />
              Daily Attendance Overview
            </h3>
            <p className="text-sm text-slate-500 mb-4">Click on any day to see detailed employee attendance</p>
            
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="text-center font-medium text-xs text-slate-500 py-2">
                  {day}
                </div>
              ))}
              
              {/* Empty cells for first week offset */}
              {calendarData.length > 0 && Array(calendarData[0].dayOfWeek).fill(null).map((_, i) => (
                <div key={`empty-${i}`} className="h-24"></div>
              ))}
              
              {/* Calendar Days */}
              {calendarData.map(dayData => (
                <div
                  key={dayData.day}
                  onClick={() => handleDateClick(dayData.dateStr, dayData.day)}
                  className="h-24 border border-slate-200 rounded-sm p-2 hover:border-primary hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`calendar-day-${dayData.day}`}
                >
                  <div className="font-mono font-bold text-sm text-slate-700 mb-1">{dayData.day}</div>
                  <div className="space-y-0.5 text-xs">
                    <div className="flex items-center text-success">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      <span>{dayData.present}</span>
                    </div>
                    <div className="flex items-center text-error">
                      <XCircle className="w-3 h-3 mr-1" />
                      <span>{dayData.absent}</span>
                    </div>
                    {dayData.halfDay > 0 && (
                      <div className="flex items-center text-warning">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        <span>{dayData.halfDay}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Attendance Sheet Table */}
          <div className="stat-card overflow-x-auto">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-primary" />
              Monthly Attendance Sheet
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-2 px-3 sticky left-0 bg-white z-10 text-xs uppercase tracking-widest">Employee</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-widest">Dept</th>
                    {Array.from({ length: attendanceSheet.total_days }, (_, i) => (
                      <th key={i + 1} className="text-center py-2 px-1 text-xs font-mono min-w-[30px]">{i + 1}</th>
                    ))}
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-widest bg-success/10">P</th>
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-widest bg-error/10">A</th>
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-widest bg-warning/10">H</th>
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-widest bg-primary/10">L</th>
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-widest bg-accent/10">OT</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSheet.attendance_sheet.map(emp => (
                    <tr key={emp.employee_id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="py-2 px-3 sticky left-0 bg-white z-10">
                        <div className="font-medium">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.employee_code}</div>
                      </td>
                      <td className="py-2 px-3 text-xs">{emp.department}</td>
                      {Array.from({ length: attendanceSheet.total_days }, (_, i) => {
                        const status = emp.days[i + 1] || "-";
                        const colorClass = STATUS_COLORS[status] || "bg-slate-100 text-slate-400";
                        return (
                          <td key={i + 1} className="py-1 px-0.5 text-center">
                            <span className={`inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 ${colorClass}`}>
                              {status}
                            </span>
                          </td>
                        );
                      })}
                      <td className="py-2 px-2 text-center font-mono font-bold text-success">{emp.summary.P}</td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-error">{emp.summary.A}</td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-warning">{emp.summary.H}</td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-primary">{emp.summary.L}</td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-accent">{emp.summary.OT}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {attendanceSheet.attendance_sheet.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Attendance Data</h3>
                <p className="text-slate-600">No attendance records found for this month.</p>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center space-x-4 mt-4 text-sm">
            <span className="text-slate-600 font-medium">Legend:</span>
            <div className="flex items-center space-x-1">
              <span className="inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 text-center bg-success text-white">P</span>
              <span>Present</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 text-center bg-error text-white">A</span>
              <span>Absent</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 text-center bg-warning text-white">H</span>
              <span>Half Day</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 text-center bg-primary text-white">L</span>
              <span>On Leave</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="inline-block w-6 h-6 rounded-sm text-xs font-bold leading-6 text-center bg-slate-100 text-slate-400">-</span>
              <span>Not Marked</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h3 className="font-heading font-bold text-xl mb-2">Select a Project</h3>
          <p className="text-slate-600">Choose a project to view attendance data.</p>
        </div>
      )}

      {/* Drilldown Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              ATTENDANCE - {selectedDate?.dateStr}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {/* Department Filter */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-sm text-sm"
                >
                  <option value="">All Departments</option>
                  {getDepartments().map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-slate-600">
                Showing {filteredDateAttendance.length} employees
              </div>
            </div>

            {/* Summary for the day */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-success/10 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-success font-mono">
                  {filteredDateAttendance.filter(e => e.status === "P").length}
                </p>
                <p className="text-xs text-success">Present</p>
              </div>
              <div className="bg-error/10 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-error font-mono">
                  {filteredDateAttendance.filter(e => e.status === "A").length}
                </p>
                <p className="text-xs text-error">Absent</p>
              </div>
              <div className="bg-warning/10 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-warning font-mono">
                  {filteredDateAttendance.filter(e => e.status === "H").length}
                </p>
                <p className="text-xs text-warning">Half Day</p>
              </div>
              <div className="bg-primary/10 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-primary font-mono">
                  {filteredDateAttendance.filter(e => e.status === "L").length}
                </p>
                <p className="text-xs text-primary">On Leave</p>
              </div>
            </div>

            {/* Employee List */}
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-widest">Employee</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-widest">Code</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-widest">Department</th>
                  <th className="text-center py-2 px-3 text-xs uppercase tracking-widest">Status</th>
                  <th className="text-center py-2 px-3 text-xs uppercase tracking-widest">OT Hours</th>
                </tr>
              </thead>
              <tbody>
                {filteredDateAttendance.map(emp => (
                  <tr key={emp.employee_id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{emp.name}</td>
                    <td className="py-2 px-3 font-mono text-sm">{emp.employee_code}</td>
                    <td className="py-2 px-3 text-sm">{emp.department}</td>
                    <td className="py-2 px-3 text-center">{getStatusBadge(emp.status)}</td>
                    <td className="py-2 px-3 text-center font-mono">
                      {emp.overtime > 0 ? (
                        <span className="text-accent font-bold">{emp.overtime}h</span>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredDateAttendance.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No attendance records for this date.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceReport;
